/* eslint-disable react/destructuring-assignment */
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData,
  renderRichText,
} from 'gatsby-source-contentful/rich-text';
import { ReactNode } from 'react';

import checklistIcon from 'assets/images/green-checkmark.svg';

import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Text from 'quarks/Text';

import RichTextParagraphBlock from 'components/RichText/RichTextParagraphBlock';

import { stringToKebabCase } from 'utils/functions';

import type { CommonNode } from '@contentful/rich-text-react-renderer';
import type { Maybe, Scalars } from 'graphqlTypes';

const scrollPosition = 'scroll-margin-top: 150px;';

export type ContentfulRichText = {
  __typename?: string;
  raw?: Maybe<Scalars['String']>;
  references?: Maybe<Array<Maybe<unknown>>>;
};

export type RichText = RenderRichTextData<ContentfulRichTextGatsbyReference>;

const getId = children =>
  stringToKebabCase(
    typeof children?.[0] === 'string' ? children?.toString() : children?.[0]?.props?.children?.toString() || '',
  );

const checklistRichTextParser = <T extends ContentfulRichText>(content: T) => {
  const options = {
    renderText: (text: ReactNode) => text,
    renderMark: {
      [MARKS.BOLD]: (text: ReactNode) => <Text fontWeight="bold">{text}</Text>,
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: CommonNode, children: ReactNode) => {
        if (Array.isArray(children) && children.length < 2 && children[0].length === 0) {
          return null;
        }

        return <RichTextParagraphBlock node={node}>{children}</RichTextParagraphBlock>;
      },
      [BLOCKS.HEADING_1]: (node: CommonNode, children: ReactNode) => (
        <Heading id={getId(children)} as="h3" textStyle="h3" css={scrollPosition} marginBottom={12}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_2]: (node: CommonNode, children: ReactNode) => (
        <Heading id={getId(children)} as="h3" textStyle="h3" css={scrollPosition} marginBottom={12}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_3]: (node: CommonNode, children: ReactNode) => (
        <Heading id={getId(children)} as="h3" textStyle="h3" css={scrollPosition} marginBottom={12}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_4]: (node: CommonNode, children: ReactNode) => (
        <Heading id={getId(children)} as="h3" textStyle="h3" css={scrollPosition} marginBottom={12}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_5]: (node: CommonNode, children: ReactNode) => (
        <Heading id={getId(children)} as="h3" textStyle="h3" css={scrollPosition} marginBottom={12}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_6]: (node: CommonNode, children: ReactNode) => (
        <Heading id={getId(children)} as="h3" textStyle="h3" css={scrollPosition} marginBottom={12}>
          {children}
        </Heading>
      ),
      [BLOCKS.UL_LIST]: (node: CommonNode, children: ReactNode) => (
        <Flex
          as="ul"
          flexDirection="column"
          gap="0.5rem"
          css={`
            list-style: none;
            padding: 30px 0 0 0;

            li.checked-list {
              p {
                color: #000;
              }
              &::before {
                content: '';
                position: absolute;
                left: 0;
                top: -4px;
                width: 30px;
                height: 30px;
                background: url(${checklistIcon});
              }
            }

            li {
              position: relative;
              padding-left: 45px;
              color: #c1c2c3;
            }
          `}
        >
          {children}
        </Flex>
      ),
      [BLOCKS.LIST_ITEM]: (node: CommonNode, children: ReactNode) => {
        const isBold = children?.[0]?.props?.children?.[0]?.props?.fontWeight === 'bold';

        return (
          <Flex
            as="li"
            flexDirection="column"
            gap="0.5rem"
            className={isBold ? 'checked-list' : ''}
            css={`
              p {
                font-size: 18px;
                margin-bottom: 15px;
              }

              p span {
                font-weight: normal;
              }
            `}
          >
            {children}
          </Flex>
        );
      },
    },
  };

  return renderRichText(content as RichText, options);
};

export default checklistRichTextParser;
